import type {DirectoryNode} from 'src/typings';
import {FileViewForm} from 'shared/constants';
import type {DirectoryContent, MediaViewerFile, OnlineFile} from './mv-types';
import {exists} from 'shared/utils/custom-utils';

export function isDirectoryNode(obj: MediaViewerFile | DirectoryNode): obj is DirectoryNode {
    return exists(obj) && 'children' in obj && exists(obj.children);
}

export function isMediaViewerFile(obj: MediaViewerFile | DirectoryNode): obj is MediaViewerFile {
    return exists(obj) && 'filename' in obj && exists(obj.filename);
}

export function isFileImage(file: MediaViewerFile | null): boolean {
    if (!exists(file)) {
        return false;
    }

    return file.viewForms.includes(FileViewForm.IMAGE);
}

export function areAllFilesMailable(files: MediaViewerFile[]): boolean {
    return files.every((file) => file.exportPermission.allowed && file.showPermission.allowed);
}

export function areAllFilesPrintable(files: MediaViewerFile[]): boolean {
    return files.every((file) => file.printPermission.allowed && file.showPermission.allowed);
}

// Sorts directory contents - directories first, then files
export function sortDirectoryContents(content: DirectoryContent[]): DirectoryContent[] {
    return content.toSorted((a, b) => {
        if (isDirectoryNode(a) && !isDirectoryNode(b)) {
            return -1;
        } else if (!isDirectoryNode(a) && isDirectoryNode(b)) {
            return 1;
        } else {
            return 0;
        }
    });
}

// Checks if a file contains certain view forms
export function mediaViewerFileContainsViewForm(file: MediaViewerFile, ...forms: FileViewForm[]): boolean {
    if (!exists(file) || !exists(file.viewForms)) {
        return false;
    }

    const contained = file.viewForms.filter((form) => forms.includes(form));
    return contained.length > 0;
}

// Function to automatically return number of columns based on set breakpoints
export const getGridViewColumnsCount = (width: number, mobile: boolean): number => {
    if (mobile) {
        return 3;
    }

    if (width > 440) {
        return 4;
    }

    if (width > 330) {
        return 3;
    }

    return 2;
};

// Recursive function to find the directory by its ID in the nested structure
export function findDirectoryInTreeById(parent: DirectoryNode, id: number, path: DirectoryNode[] = []): DirectoryNode | null {
    if (parent.id === id) {
        path.push(parent);
        return parent;
    }

    for (const childDirectory of parent.children) {
        const newPath = [...path, parent]; // Append current directory to the path
        const result = findDirectoryInTreeById(childDirectory, id, newPath);

        if (result) {
            path.push(...newPath.slice(path.length)); // Update path with newPath elements
            return result;
        }
    }

    return null;
}

export function isOnlineFile(file: MediaViewerFile): file is OnlineFile {
    return exists(file) && 'online' in file && file.online && 'sourceUrl' in file && exists(file.sourceUrl);
}